import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet } from 'api-service';
import { getRequestTimeDuration } from 'utils';
import {
  voiceShareAdapter,
  profilesAdapter,
  commentAdapter,
  contentAdapter,
  profileOverviewAdapter,
  commentEmojiAdapter,
  voiceTrendAdapter,
  storyPublicationComparisonAdapter,
  storyPublicationChartAdapter,
  brandNssAdapter,
  brandNssTrendAdapter,
} from './brandBaan.adapter';
import { getVoiceTrendApiPlatform } from './brandBaan.helpers';

export const getBrandBaanProfiles = createAsyncThunk(
  'brandBaanSlice/getBrandBaanProfiles',
  async (platform, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;

    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      platform,
    };

    try {
      const response = await apiGet(
        '/search/api/v2/brand-identity/charts/resources',
        defaultParams
      );

      return profilesAdapter(response?.data?.result?.data, defaultParams);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanVoiceShare = createAsyncThunk(
  'brandBaanSlice/getBrandBaanVoiceShare',
  async (platform, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;
    const { data } = thunkAPI.getState().brandBaanReducer.checkStatus;

    const brandQuerires = data ? data.brandsQuerires : [];
    const apiPlatform = getVoiceTrendApiPlatform(platform);

    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      ...(apiPlatform ? { platforms: [apiPlatform] } : {}),
    };

    try {
      const response = await apiGet(
        '/search/api/v2/brand-identity/charts/voice-share',
        defaultParams
      );

      return voiceShareAdapter(response?.data?.result?.data, brandQuerires);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanContents = createAsyncThunk(
  'brandBaanSlice/getBrandBaanContents',
  async ({ competitor }, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;
    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      competitor_id: competitor?.id,
    };

    try {
      const response = await apiGet(
        '/search/api/v2/brand-identity/charts',
        defaultParams
      );

      return contentAdapter(response?.data?.result?.data, competitor);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanComments = createAsyncThunk(
  'brandBaanSlice/getBrandBaanComments',
  async ({ competitor }, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;
    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      platforms: ['instagram-comments'],
      competitor_id: competitor?.id,
    };

    try {
      const response = await apiGet(
        '/search/api/v2/brand-identity/charts',
        defaultParams
      );

      return commentAdapter(response?.data?.result?.data, competitor);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
export const checkBrandStatus = createAsyncThunk(
  'brandBaanSlice/checkBrandStatus',
  async (_, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;

    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
    };

    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/charts/check',
        defaultParams
      );

      if (response?.status !== 200) {
        return {
          hasData: false,
          queryId: null,
          fromDate: null,
          toDate: null,
        };
      }

      const hasData =
        response?.data?.result?.data &&
        response.data.result.data?.message?.toLowerCase().includes('ok');

      const brandsQuerires = [
        response?.data?.result?.data?.data?.my_brand_query,
        ...response?.data?.result?.data?.data?.competitor_queries,
      ];

      return {
        hasData,
        myBrandId: response?.data?.result?.data?.data?.my_brand_query?.brand_id,
        queryId: response.data.result.data?.data?.query_id,
        fromDate: response.data.result.data?.data?.histories_time_frame?.from,
        toDate: response.data.result.data?.data?.histories_time_frame?.to,
        brandsQuerires,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanBrands = createAsyncThunk(
  'brandBaanSlice/getBrandBaanBrands',
  async (_, thunkAPI) => {
    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/charts/competitors'
      );

      return response?.data?.result?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanReportage = createAsyncThunk(
  'brandBaanSlice/getBrandBaanReportage',
  async (params, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;
    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      sort: 'time',
      ...params,
    };
    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/reportage/list',
        defaultParams
      );

      return response?.data?.result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
export const getBrandBaanReportageKeywordList = createAsyncThunk(
  'brandBaanSlice/getBrandBaanReportageKeywordList',
  async (params, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;
    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      sort: 'count',
      ...params,
    };

    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/reportage/keywords',
        defaultParams
      );
      return {
        data: response?.data?.result?.data || [],
        meta: response?.data?.result?.meta || {},
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getReportageListByKeyword = createAsyncThunk(
  'brandBaanSlice/getReportageListByKeyword',
  async (params, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;
    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
    };

    // eslint-disable-next-line camelcase
    const {
      reportage_anchor_texts: reportageAnchorText,
      count,
      page,
    } = params || {};
    const url = reportageAnchorText?.url;
    const keyword = reportageAnchorText?.keyword;

    const queryParams = {
      ...defaultParams,
      'reportage_anchor_texts[url]': url,
      'reportage_anchor_texts[keyword]': keyword,
      api_path: '/news/posts',
      count,
      page,
    };

    try {
      const response = await apiGet('/search/api/v1/search', queryParams);
      const result = response?.data?.result;
      return {
        data: result?.data || [],
        total: result?.meta?.total ?? result?.total ?? 0,
        page: result?.meta?.page ?? result?.page ?? 1,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanProfileOverview = createAsyncThunk(
  'brandBaanSlice/getBrandBaanProfileOverview',
  async (_, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;
    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
    };
    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/charts/overview',
        defaultParams
      );

      return profileOverviewAdapter(response?.data?.result?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanCommnetsEmojis = createAsyncThunk(
  'brandBaanSlice/getBrandBaanCommnetsEmojis',
  async (_, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;
    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      platform: 'instagram-comments',
      count: 35,
    };
    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/on-demand-charts/emojis',
        defaultParams
      );

      return commentEmojiAdapter(response?.data?.result);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanVoiceTrend = createAsyncThunk(
  'brandBaanSlice/getBrandBaanVoiceTrend',
  async ({ competitor, platform, interval }, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;
    const apiPlatform = getVoiceTrendApiPlatform(platform);

    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      competitor_id: competitor?.id,
      interval,
      ...(apiPlatform ? { platforms: [apiPlatform] } : {}),
    };
    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/charts/share-of-voice',
        defaultParams
      );

      return voiceTrendAdapter(
        response?.data?.result?.data,
        competitor?.name,
        platform
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanNss = createAsyncThunk(
  'brandBaanSlice/getBrandBaanNss',
  async ({ platform }, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;
    const apiPlatform = getVoiceTrendApiPlatform(platform);

    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      chart_types: ['net_sentiment_score'],
      ...(apiPlatform ? { platforms: [apiPlatform] } : {}),
    };

    try {
      const response = await apiGet(
        '/search/api/v2/brand-identity/charts',
        defaultParams
      );

      return brandNssAdapter(response?.data?.result?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanNssTrend = createAsyncThunk(
  'brandBaanSlice/getBrandBaanNssTrend',
  async ({ competitor, platform, interval }, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;
    const apiPlatform = getVoiceTrendApiPlatform(platform);

    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      competitor_id: competitor?.id,
      interval,
      ...(apiPlatform ? { platforms: [apiPlatform] } : {}),
    };

    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/charts/net-sentiment-score',
        defaultParams
      );

      return brandNssTrendAdapter(
        response?.data?.result?.data,
        competitor?.name,
        platform
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanStoryPublicationComparison = createAsyncThunk(
  'brandBaanSlice/getBrandBaanStoryPublicationComparison',
  async (params, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;

    const { storyPublicationComparison } =
      thunkAPI.getState().chartReducer['brandBaan'].allPlatforms;

    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      interval: storyPublicationComparison,
      my_brand_id:
        thunkAPI.getState().brandBaanReducer?.brands?.data?.my_brand?.id,
      ...params,
    };
    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/instagram/stories/publication_comparison',
        defaultParams
      );
      return storyPublicationComparisonAdapter(response?.data?.result.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanStoryPublishment = createAsyncThunk(
  'brandBaanSlice/getBrandBaanStoryPublishment',
  async ({ myBrandId }, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;

    const defaultParams = {
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
      my_brand_id: myBrandId,
    };

    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/instagram/stories/publication_comparison/chart',
        defaultParams
      );

      return storyPublicationChartAdapter(response?.data?.result.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanStoryAnalytics = createAsyncThunk(
  'brandBaanSlice/getBrandBaanStoryAnalytics',
  async ({ brandId, filter }, thunkAPI) => {
    const { currentDate } = thunkAPI.getState().pageReducer;

    const defaultParams = {
      brand_id: brandId,
      filter,
      from: currentDate.timeDuration ? undefined : currentDate.fromDate,
      to: currentDate.timeDuration ? undefined : currentDate.toDate,
      time_duration: getRequestTimeDuration(currentDate),
    };

    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/instagram/stories/analytics',
        defaultParams
      );

      return response?.data?.result?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getBrandBaanDetails = createAsyncThunk(
  'brandBaanSlice/getBrandBaanDetails',
  async (_, thunkAPI) => {
    try {
      const response = await apiGet(
        '/search/api/v1/brand-identity/entities/user/brand-info'
      );
      return response?.data?.result?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
